import React, { useState, useEffect } from 'react';
import { documentiApi, usersApi, immobiliApi, contrattiApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, 
  Search, 
  Download,
  Trash2, 
  FolderOpen,
  FileText,
  User,
  Home,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const tipiDocumento = [
  { value: 'contratto', label: 'Contratto' },
  { value: 'identita', label: 'Documento Identità' },
  { value: 'reddito', label: 'Documento di Reddito' },
  { value: 'catastale', label: 'Visura Catastale' },
  { value: 'altro', label: 'Altro' },
];

const DocumentiPage = () => {
  const [documenti, setDocumenti] = useState([]);
  const [users, setUsers] = useState([]);
  const [immobili, setImmobili] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEntita, setFilterEntita] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'altro',
    entita_tipo: 'user',
    entita_id: '',
    file: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docRes, usersRes, immRes, contRes] = await Promise.all([
        documentiApi.getAll(),
        usersApi.getAll(),
        immobiliApi.getAll(),
        contrattiApi.getAll()
      ]);
      setDocumenti(docRes.data);
      setUsers(usersRes.data.filter(u => u.ruolo !== 'admin'));
      setImmobili(immRes.data);
      setContratti(contRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error('Seleziona un file');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('nome', formData.nome);
      data.append('tipo', formData.tipo);
      data.append('entita_tipo', formData.entita_tipo);
      data.append('entita_id', formData.entita_id);

      await documentiApi.upload(data);
      toast.success('Documento caricato');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentiApi.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    try {
      await documentiApi.delete(id);
      toast.success('Documento eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'altro',
      entita_tipo: 'user',
      entita_id: '',
      file: null
    });
  };

  const getEntitaInfo = (doc) => {
    switch (doc.entita_tipo) {
      case 'user':
        const user = users.find(u => u.id === doc.entita_id);
        return user ? `${user.nome} ${user.cognome}` : '-';
      case 'immobile':
        const imm = immobili.find(i => i.id === doc.entita_id);
        return imm ? `${imm.indirizzo}, ${imm.citta}` : '-';
      case 'contratto':
        const cont = contratti.find(c => c.id === doc.entita_id);
        if (!cont) return '-';
        const inq = users.find(u => u.id === cont.inquilino_id);
        return inq ? `Contratto ${inq.nome} ${inq.cognome}` : 'Contratto';
      default:
        return '-';
    }
  };

  const getEntitaIcon = (tipo) => {
    switch (tipo) {
      case 'user': return User;
      case 'immobile': return Home;
      case 'contratto': return FileText;
      default: return FolderOpen;
    }
  };

  const getEntitaOptions = () => {
    switch (formData.entita_tipo) {
      case 'user':
        return users.map(u => ({ id: u.id, label: `${u.nome} ${u.cognome}` }));
      case 'immobile':
        return immobili.map(i => ({ id: i.id, label: `${i.indirizzo}, ${i.citta}` }));
      case 'contratto':
        return contratti.map(c => {
          const inq = users.find(u => u.id === c.inquilino_id);
          return { id: c.id, label: inq ? `Contratto ${inq.nome} ${inq.cognome}` : 'Contratto' };
        });
      default:
        return [];
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredDocumenti = documenti.filter(d => {
    const matchSearch = d.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEntita = filterEntita === 'all' || d.entita_tipo === filterEntita;
    return matchSearch && matchEntita;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="documenti-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documenti</h1>
            <p className="text-slate-500 mt-1">Gestisci i documenti di utenti, immobili e contratti</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowDialog(true); }}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="upload-documento-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Carica Documento
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca documenti..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-documenti"
                />
              </div>
              <Select value={filterEntita} onValueChange={setFilterEntita}>
                <SelectTrigger className="w-[180px]" data-testid="filter-entita">
                  <SelectValue placeholder="Tipo entità" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le entità</SelectItem>
                  <SelectItem value="user">Utenti</SelectItem>
                  <SelectItem value="immobile">Immobili</SelectItem>
                  <SelectItem value="contratto">Contratti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Associato a</TableHead>
                  <TableHead>Dimensione</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocumenti.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Nessun documento trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocumenti.map((doc) => {
                    const Icon = getEntitaIcon(doc.entita_tipo);
                    return (
                      <TableRow key={doc.id} data-testid={`documento-row-${doc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{doc.nome}</p>
                              <p className="text-sm text-slate-500">{doc.filename}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {tipiDocumento.find(t => t.value === doc.tipo)?.label || doc.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Icon className="w-4 h-4" />
                            {getEntitaInfo(doc)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatFileSize(doc.size)}
                        </TableCell>
                        <TableCell>
                          {new Date(doc.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica Documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome Documento *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Contratto di locazione firmato"
                required
                data-testid="input-nome-doc"
              />
            </div>

            <div>
              <Label>Tipo Documento *</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => setFormData({...formData, tipo: v})}
              >
                <SelectTrigger data-testid="select-tipo-doc">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tipiDocumento.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Associa a *</Label>
              <Select 
                value={formData.entita_tipo} 
                onValueChange={(v) => setFormData({...formData, entita_tipo: v, entita_id: ''})}
              >
                <SelectTrigger data-testid="select-entita-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utente</SelectItem>
                  <SelectItem value="immobile">Immobile</SelectItem>
                  <SelectItem value="contratto">Contratto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Seleziona {formData.entita_tipo === 'user' ? 'Utente' : formData.entita_tipo === 'immobile' ? 'Immobile' : 'Contratto'} *</Label>
              <Select 
                value={formData.entita_id} 
                onValueChange={(v) => setFormData({...formData, entita_id: v})}
              >
                <SelectTrigger data-testid="select-entita-id">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {getEntitaOptions().map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>File *</Label>
              <Input
                type="file"
                onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                className="cursor-pointer"
                data-testid="input-file"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={uploading || !formData.entita_id || !formData.file}
                data-testid="submit-documento"
              >
                {uploading ? 'Caricamento...' : 'Carica'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentiPage;
